import type { InputHTMLAttributes, ReactNode, Ref } from "react";

import { Button } from "./Button";
import { control, type ControlContext, type ControlSize, type ControlVariant } from "./control";
import { FormField } from "./FormField";
import { CloseIcon } from "@/ui/icons";

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "size"> & {
  label: string;
  /**
   * Reaches the `<input>`, not the wrapper. Needed by any form that focuses its
   * first field itself — the workout editors both re-focus after a submit.
   */
  ref?: Ref<HTMLInputElement>;
  /** Quiet guidance shown under the field. */
  hint?: ReactNode;
  /** Replaces the hint and turns the field red. */
  error?: ReactNode;
  size?: ControlSize;
  /** The container this field sits in — see {@link control}. */
  on?: ControlContext;
  /** Shared filled material field or `plain`, for a field that *is* a list row. */
  variant?: ControlVariant;
  /** A leading glyph inside the fill — a magnifier, a currency mark. */
  leading?: ReactNode;
  /**
   * Draws the iOS clear ✕ whenever `value` is non-empty. Passing this is what
   * turns the affordance on; the field cannot invent one, because clearing a
   * field it does not own the state of would be a lie.
   */
  onClear?: () => void;
  /** Applied to the wrapper, not the input. Use `className` for the input. */
  fieldClassName?: string;
};

/**
 * A labelled text field: label, control, and one line of hint or error under it.
 *
 * This is the primitive for any field that carries its own label, which is most
 * of them — sign-in, the new-project dialog. Fields that share a label with a
 * sibling (the `sets × reps` pair) or that sit inside a larger composed control
 * reach for {@link control} directly instead; both routes end at the same class
 * list, so they cannot drift apart again.
 *
 * The label/hint/error layout is {@link FormField}'s, not its own — that
 * arrangement used to be written out here *and* privately inside `CardEditor`,
 * which is how the app ended up with two different distances between a field
 * and its error message.
 *
 * ---------------------------------------------------------------------------
 * The desktop field, and the focus state it does not own
 * ---------------------------------------------------------------------------
 * It is paper with a solved hairline edge in light and a sunken well with the
 * same boundary contract in dark (`COMPACT-DESIGN.md` §5.3). `index.css`
 * applies the single 2px accent `:focus-visible` outline globally; it is not
 * re-declared here or in `control.ts`.
 *
 * The leading glyph and the clear ✕ are drawn *over* the fill rather than
 * beside it, and the input's padding is grown to make room. Putting them in a
 * flex row beside the input is how a field ends up with two focus rings and a
 * click target that misses by 8px.
 */
export function TextField({
  label,
  hint,
  error,
  className,
  fieldClassName,
  required,
  size = "md",
  on = "panel",
  variant = "filled",
  leading,
  onClear,
  ref,
  ...rest
}: TextFieldProps) {
  const clearable = onClear !== undefined && String(rest.value ?? "") !== "";

  return (
    <FormField
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative flex items-center">
          {leading ? (
            <span
              aria-hidden
              className="pointer-events-none absolute left-3 flex items-center text-muted"
            >
              {leading}
            </span>
          ) : null}

          <input
            id={id}
            ref={ref}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={control({
              size,
              on,
              variant,
              invalid,
              leading: Boolean(leading),
              trailing: clearable,
              className,
            })}
            {...rest}
          />

          {clearable ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onClear}
              aria-label={`Clear ${label.toLowerCase()}`}
              // The dense 32px footprint fits inside the field; the halo keeps
              // the coarse-pointer target at 44px.
              className="absolute right-3"
            >
              <CloseIcon className="size-4" />
            </Button>
          ) : null}
        </div>
      )}
    </FormField>
  );
}
